/**
 * Type definitions for event properties used in analytics tracking.
 */

import type { T_Optional } from '@tryiris-ai/js-types';
import { z } from 'zod';
import { DEVICE_PROPERTIES } from '../constants/event-properties.js';

export interface AgentProperties {
  /** @deprecated Use `agent_id`; retained for the parity window. */
  agentId?: string;
  agent_id?: string;
  agentName?: string;
  agentVersion?: string;
  agentStatus?: string;
  agentEmbedded?: boolean;
  is_embedded?: boolean;
}

export interface CommonProperties {
  /** @deprecated Use `user_id`; retained for the parity window. */
  userId?: string;
  user_id?: string;
  last_known_user_id?: string;
  /** @deprecated Use `session_id`; retained for the parity window. */
  sessionId?: string;
  session_id?: string;
  /** @deprecated Use `env`, the canonical spelling in canonical-event-map.json. */
  environment?: string;
  env?: string;
  vendor?: string;
  platform?: string;
  app_version?: string;
  // Optional fields preserve compatibility while the resolver fills the envelope.
  client?: string;
  process_type?: string;
  boot_id?: string;
  launch_id?: string;
  auth_journey_id?: string;
  source?: string;
  surface?: string;
  linking_surface?: string;
  // ENG-4324 (SCR-1): the room a named screen_* event belongs to — the "which
  // room" identity Product wants. Canonical snake_case, matching the existing
  // room_id on bridge_link_succeeded. Also already carried by auto-link events
  // through the index signature; typed here so screen call sites are checked.
  room_id?: string;
  // ENG-4312: severity classification (e.g. 'S3/warn' on discovery_empty) — see
  // EVENT_SEVERITY in constants/event-properties.ts for the sanctioned values.
  severity?: string;
  result?: string;
  value?: unknown;
  command?: string;
  boot_duration_ms?: number;
  error_message?: string;
  error_type?: string;
  command_key?: string;
  commandKey?: string;
  modal_instance_id?: string;
  added_at?: string;
  uptime_seconds?: number;
  tool_used?: string;
  quality_score?: number;
  subject_source?: string;
  reason?: string;
  shadow_source_agent_id?: string;
  shadow_target_agent_id?: string;
  // Per ENG-3960 §E12 — explicit error-flag cohort props so media_quality_ping
  // (and any error-bearing event) is queryable as an is_error_state cohort.
  is_error_state?: boolean;
  // OAuth/identity provider name (e.g. 'auth0', 'google'); used by oauth_error events.
  provider?: string;
  /** Resolved boolean value of a feature flag (ENG-3797 verification gate). */
  flag_value?: boolean;
  /** Why a gated decision was reached — never carries raw PII (ENG-3797). */
  decision_reason?: string;
}

/**
 * Canonical device-cohort property keys (ENG-3960 §E12, consistent with ENG-3479
 * PostHog group props `name`/`brand_name`). Every device-scoped event standardizes
 * on EXACTLY these: the two IDs power cohort breakdowns, the two *_name props the
 * human-readable labels.
 *
 * Per rule: think-in-systems — one canonical shape, enforced at the type boundary.
 */
export const CANONICAL_DEVICE_PROPERTY_KEYS = [
  DEVICE_PROPERTIES.DEVICE_ID_CANONICAL,
  DEVICE_PROPERTIES.DEVICE_MODEL_ID,
  DEVICE_PROPERTIES.MODEL_NAME,
  DEVICE_PROPERTIES.DEVICE_BRAND_ID,
  DEVICE_PROPERTIES.BRAND_NAME
] as const;

/**
 * Off-convention device keys forbidden by the canonical convention. These are the
 * pre-ENG-3960 aliases (`device_brand`/`device_model`) and bare/camelCase drift
 * (`brand`/`model`/`brandId`/`modelId`). They are rejected at all three levels:
 * tsc (CanonicalDeviceProperties), ESLint (canonical-device-analytics-props), and
 * runtime (assertCanonicalDeviceProps in track.ts).
 */
export const OFF_CONVENTION_DEVICE_PROPERTY_KEYS = [
  'model',
  'modelId',
  'device_model',
  'brand',
  'brandId',
  'device_brand'
] as const;

export type OffConventionDeviceKey = (typeof OFF_CONVENTION_DEVICE_PROPERTY_KEYS)[number];

/**
 * The enforced, canonical device-properties shape. New device-scoped capture sites
 * type their props with this interface; the off-convention keys are typed `never`
 * so `tsc` fails the build the moment a forbidden key is supplied.
 *
 * Per rule: ai-code-requires-tests — proven by the `@ts-expect-error` fixture in
 * canonical-device-props.type-test.ts.
 */
export interface CanonicalDeviceProperties {
  device_id?: string;
  device_model_id?: string;
  model_name?: string;
  device_brand_id?: string;
  brand_name?: string;
  // Forbidden off-convention keys — `never` makes tsc reject any non-undefined value.
  model?: never;
  modelId?: never;
  device_model?: never;
  brand?: never;
  brandId?: never;
  device_brand?: never;
}

export interface DeviceProperties {
  /** @deprecated Use `device_id`; retained for the parity window. */
  deviceId?: string;
  device_id?: string;
  deviceName?: string;
  deviceType?: string;
  // Canonical device-cohort props (ENG-3960 §E12). Prefer these everywhere.
  device_brand_id?: string;
  brand_name?: string;
  device_model_id?: string;
  model_name?: string;
  target_device_id?: string;
  target_bridge_id?: string;
  source_device_id?: string;
  /** @deprecated ENG-3960 §E12 — use `brand_name`. Retained for the Release-A dual-write window. */
  brand?: string;
  /** @deprecated ENG-3960 §E12 — use `model_name`. Retained for the Release-A dual-write window. */
  model?: string;
  /** @deprecated ENG-3960 §E12 — use `brand_name`. Retained for the Release-A dual-write window. */
  device_brand?: string;
  /** @deprecated ENG-3960 §E12 — use `model_name`. Retained for the Release-A dual-write window. */
  device_model?: string;
  controlProtocol?: string;
  is_embedded?: boolean;
  media_type?: string;
  permissions_modal_count?: number;
  connection_status?: number;
}

/**
 * discovery_mismatch and discovery_empty properties. See DISCOVERY_PROPERTIES
 * in constants/event-properties.ts for the canonical key strings.
 */
export interface DiscoveryMismatchProperties {
  primary_protocol?: string;
  secondary_protocol?: string;
  primary_name?: string;
  secondary_name?: string;
  pre_filter_count?: number;
}

/**
 * discovery_empty must carry the raw count. EventProperties wraps this
 * interface in Partial, so an optional field here is not a type error at
 * the emit site. This intersection is what makes a missing count fail tsc.
 */
export type DiscoveryEmptyProperties = EventProperties & {
  pre_filter_count: number;
};

export interface StudioProperties {
  /** @deprecated Use `studio_id`; retained for the parity window. */
  studioId?: string | undefined;
  studio_id?: string | undefined;
  studioName?: string;
}

export interface OrganizationProperties {
  /** Canonical snake_case organization id, as the canonical event map requires. */
  org_id?: string;
  orgId?: string;
  orgName?: string;
}

export interface BridgeProperties {
  bridgeType?: 'local' | 'secondary';
  studioId: string;
  requestId: string;
  agentIdRequestingToLinkTo: string;
}

export interface CollaborationProperties {
  inviteeEmail?: string;
  inviteeRole?: string;
}

export interface BillingProperties {
  trial_id?: string;
  trial_end?: string;
  product?: 'iris' | 'hive';
  planType?: string;
  billingFrequency?: string;
  invoiceId?: string;
  paymentMethodType?: string;
}

export interface ControllerProperties {
  controllerId?: string;
  controllerType?: string;
  controllerName?: string;
  hotkeyCommand?: string;
  hotkeyName?: string;
  ipAddress?: string;
  port?: number;
}

export interface MovementToolsProperties {
  toolName?: string;
  action?: string;
  action_id?: string;
  driver_kind?: 'generic' | 'catalog' | 'unknown';
  presetId?: string;
  presetName?: string;
  presetGroupId?: string;
  presetGroupName?: string;
  timeSpent?: number;
  focusSpeed?: number;
  joystickDirection?: string;
  joystickIntensity?: number;
  shotType?: string;
}

export interface RecordingProperties {
  deviceIds?: string[];
  recordingPath?: string;
  recordingDuration?: number;
}

export interface SecurityProperties {
  method?: string;
  success?: boolean;
}

export interface SharingProperties {
  permissionType?: string;
  expirationTime?: Date;
  inviteeEmail?: string;
  inviteeRole?: string;
  linkType?: string;
}

export interface VideoModesProperties {
  mode?: string;
  previousMode?: string;
  isLocked?: boolean;
  success?: boolean;
  agentIds?: string[];
}

export interface VideoSourcesProperties {
  sourceType?: string;
  sourceProtocol?: string;
  sourceId?: string;
  sourceName?: string;
  sourceIp?: string;
  sourcePort?: number;
  sourceUrl?: string;
  brand?: string;
  model?: string;
  controlType?: string;
  passwordRequired?: boolean;
  settingsConfirmed?: boolean;
  sleepMode?: boolean;
  viewType?: string;
  /**
   * RTSP Assisted Setup: whether the user manually edited the Stream URL
   * (vs the guided/composed URL). Boolean only — the URL itself is NEVER
   * attached (it can embed `rtsp://user:pass@host` credentials).
   */
  hasManualUrl?: boolean;
  /**
   * RTSP Check-Connectivity outcome, distinguishing a credentials rejection
   * (`auth_failed`) from a host-unreachable / stream error
   * (`connection_failed`) — today both collapse into a generic connect
   * error. `success` when the stream connected.
   */
  connectivityOutcome?: 'success' | 'auth_failed' | 'connection_failed';
}

export interface WorkspaceProperties {
  orgId?: string;
  orgName?: string;
  studioId?: string;
  studioName?: string;
  website?: string;
  industry?: string;
  photoUrl?: string;
  previousValue?: string;
}

/**
 * Prequalification Zod schemas — single source of truth.
 * Each schema provides: .enum (value-keyed object), .options (array), z.infer (type).
 */
// Per ENG-4171 — the first prequal question became a PTZ-camera count range
// (was a yes/no/unsure "do you have cameras"). The schema's literal values double
// as the wizard option values, so PTZ_COUNT is keyed by the ranges themselves
// (e.g. PTZ_COUNT['1-3']). Per rule: stay-in-scope — the rename is the change.
export const ptzCountSchema = z.enum(['0', '1-3', '4-7', '8+']);
export const networkMatchSchema = z.enum(['yes', 'no', 'unsure']);
export const networkTypeSchema = z.enum(['home', 'enterprise']);
// Per ENG-4171 — the platform question (desktop vs mobile) was removed entirely,
// so accessDevice (schema/type/const) is gone and 'wrong_device' is dropped from
// the qualification buckets: its only producer was the now-deleted mobile check.
export const qualificationBucketSchema = z.enum(['ready', 'no_cameras', 'wrong_network']);

export type PtzCount = z.infer<typeof ptzCountSchema>;
export type NetworkMatch = z.infer<typeof networkMatchSchema>;
export type NetworkType = z.infer<typeof networkTypeSchema>;
export type QualificationBucket = z.infer<typeof qualificationBucketSchema>;

export const PTZ_COUNT = ptzCountSchema.enum;
export const NETWORK_MATCH = networkMatchSchema.enum;
export const NETWORK_TYPE = networkTypeSchema.enum;
export const QUALIFICATION_BUCKETS = qualificationBucketSchema.enum;

export const prequalSchema = z.object({
  ptzCount: ptzCountSchema,
  networkMatch: networkMatchSchema,
  networkType: networkTypeSchema
});

export type PrequalificationAnswers = z.infer<typeof prequalSchema>;

export type PrequalificationProperties = Partial<PrequalificationAnswers> & {
  qualificationBucket?: QualificationBucket;
};

/**
 * Download-start property value maps (ENG-4347). `as const` maps rather than
 * bare strings so call sites never carry raw literals and tsc rejects values
 * outside the agreed vocabulary — the same discipline EVENT_NAMES enforces
 * for event names.
 *
 * The web values (ENG-4328) exist because the desktop app cannot record its
 * own download: a first-time user is necessarily in a browser at that moment.
 * `in_app_setup` therefore means a RE-download from the installed app, and the
 * two must never collapse to one value or journey stage 1 becomes unqueryable.
 */
export const DOWNLOAD_SOURCES = {
  IN_APP_SETUP: 'in_app_setup',
  WEB_DOWNLOAD_PAGE: 'web_download_page'
} as const;

export type DownloadSource = (typeof DOWNLOAD_SOURCES)[keyof typeof DOWNLOAD_SOURCES];

export const DOWNLOAD_ENTRY_POINTS = {
  AGENT_SETUP_WIZARD: 'agent_setup_wizard',
  WEB_SETUP_WIZARD: 'web_setup_wizard'
} as const;

export type DownloadEntryPoint = (typeof DOWNLOAD_ENTRY_POINTS)[keyof typeof DOWNLOAD_ENTRY_POINTS];

/** The surfaces the shared agent-setup download page can render on. */
export const DOWNLOAD_SURFACES = {
  ELECTRON_APP: 'electron_app',
  WEB: 'web'
} as const;

export type DownloadSurface = (typeof DOWNLOAD_SURFACES)[keyof typeof DOWNLOAD_SURFACES];

/** The (source, entry point) pair a single surface reports. */
export interface DownloadOrigin {
  download_source: DownloadSource;
  download_entry_point: DownloadEntryPoint;
}

/**
 * Surface → the origin pair it reports. Call sites spread a whole entry rather
 * than assembling a source and an entry point separately, so a mismatched pair
 * (web source with the in-app entry point, say) cannot be expressed. `satisfies`
 * keeps the map total: adding a surface without declaring its origin, or naming
 * a value outside the maps above, is a compile error rather than a wrong row in
 * PostHog weeks later.
 */
export const DOWNLOAD_ORIGIN_BY_SURFACE = {
  [DOWNLOAD_SURFACES.ELECTRON_APP]: {
    download_source: DOWNLOAD_SOURCES.IN_APP_SETUP,
    download_entry_point: DOWNLOAD_ENTRY_POINTS.AGENT_SETUP_WIZARD
  },
  [DOWNLOAD_SURFACES.WEB]: {
    download_source: DOWNLOAD_SOURCES.WEB_DOWNLOAD_PAGE,
    download_entry_point: DOWNLOAD_ENTRY_POINTS.WEB_SETUP_WIZARD
  }
} as const satisfies Record<DownloadSurface, DownloadOrigin>;

/**
 * Properties carried by the download-start event family (ENG-4347).
 * `download_attempt_id` is synthesized per initiation by the
 * download-lifecycle tracker so the canonical and legacy dual-write rows can
 * be joined 1:1 during the PostHog parity check.
 */
export interface DownloadProperties {
  download_source?: DownloadSource;
  download_entry_point?: DownloadEntryPoint;
  download_attempt_id?: string;
  // ENG-4310 install-failure trio. `operating_system` / `architecture` use the
  // names locked by the download property contract in canonical-event-map.json;
  // they are the platform the user ASKED for, not the host, which is exactly the
  // diagnostic a failure row needs.
  installer_failure_reason?: InstallerFailureReason;
  operating_system?: string;
  architecture?: string;
}

/**
 * Why a stage-1 install never happened (ENG-4310). These strings are the
 * breakdown values on the stage-1 dashboard, so they are an `as const` map for
 * the same reason the event names are: a raw literal at a call site is a typo
 * away from silently splitting one failure mode into two series.
 *
 * ONE reason, deliberately. A reason only belongs here once some surface can
 * actually observe it AND a test drives it, because a value that never appears
 * reads on a dashboard as "this never happens" rather than "we cannot see this".
 * Two candidates were considered and rejected for exactly that:
 *   • a blocked download initiation — the renderer's synthetic `link.click()`
 *     returns normally when Chromium refuses the transfer by policy; the refusal
 *     is only observable from the main process's `will-download` / download-item
 *     state, so it needs a download-monitor surface, not a `catch` here.
 *   • a security suite quarantining the artifact after a successful transfer —
 *     invisible from the initiating surface for the same reason.
 * Both add their reason here when that surface exists, rather than renaming the
 * event.
 */
export const INSTALLER_FAILURE_REASONS = {
  /** We ship no artifact for the requested OS/architecture, so no install is possible. */
  ARTIFACT_UNAVAILABLE: 'artifact_unavailable'
} as const;

export type InstallerFailureReason =
  (typeof INSTALLER_FAILURE_REASONS)[keyof typeof INSTALLER_FAILURE_REASONS];

/**
 * Placeholder for a download target we could not read at all (ENG-4310). Shared
 * so both vendor setup pages report the same value: `operating_system` and
 * `architecture` are required properties, and a dropped property would have the
 * canonical receipt reject the row outright — losing the failure entirely rather
 * than recording an unidentifiable one. Also the architecture reported from the
 * OS picker, which fails before any architecture has been chosen.
 */
export const UNKNOWN_DOWNLOAD_TARGET = 'unknown';

/**
 * Onboarding-wizard properties (ENG-4305, plan ONB).
 *
 * `onboarding_stage` + `onboarding_step` are the funnel dimensions every wizard
 * event carries, so one event name plus these two keys reconstructs the whole
 * screen-by-screen funnel. The `$exception_*` keys are PostHog-owned spellings
 * (POSTHOG_EXCEPTION_PROPERTY_NAMES) declared here so a failure envelope is
 * typed rather than falling through to the metadata index signature.
 */
export interface OnboardingProperties {
  onboarding_stage?: string;
  onboarding_step?: string;
  add_method?: string;
  recoverable?: boolean;
  terminal?: boolean;
  $exception_level?: string;
  $exception_source?: string;
}

/**
 * Spec 4 — per-document renderer startup diagnostics (renderer_startup_phase /
 * renderer_startup_measured).
 *
 * `renderer_load_id` identifies ONE document; `boot_id` (CommonProperties) stays
 * as launch correlation only. Scalars only — no raw pathname, query string,
 * room/org id or auth payload ever reaches this envelope; `initial_route_kind`
 * is a coarse route shape for exactly that reason.
 *
 * `span_kind` is load-bearing for correct accounting: only `phase` rows on the
 * `document` lane form a disjoint partition that may be summed. `operation` rows
 * overlap by design (concurrent requests) and `milestone` rows are points.
 */
export interface RendererStartupProperties {
  renderer_load_id?: string;
  span_kind?: string;
  lane?: string;
  phase?: string;
  sequence?: number;
  start_ms?: number;
  end_ms?: number;
  duration_ms?: number;
  outcome?: string;
  renderer_startup_duration_ms?: number;
  initial_route_kind?: string;
  navigation_type?: string;
  endpoint?: string;
  destination?: string;
  span_count?: number;
  dropped_span_count?: number;
  // True when this document's operation/milestone rows were also sent; the
  // summary and document-lane phases ship for every document regardless.
  detail_sampled?: boolean;
  // `null` is a real value here: it means "not observable / not yet fired",
  // which a 0 would misreport as "free" (review-4 §4).
  nav_response_end_ms?: number | null;
  nav_dom_content_loaded_ms?: number | null;
  nav_load_event_end_ms?: number | null;
  loaded_js_count?: number | null;
  loaded_js_transfer_bytes?: number | null;
  loaded_js_encoded_bytes?: number | null;
  loaded_js_decoded_bytes?: number | null;
  loaded_js_sizes_available?: boolean;
  long_task_count?: number | null;
  long_task_total_ms?: number | null;
  long_task_max_ms?: number | null;
  timer_sample_count?: number;
  timer_lateness_max_ms?: number | null;
  timer_lateness_p95_ms?: number | null;
  hidden_ms?: number;
}

export interface MetadataProperties {
  [key: string]: any;
}

/**
 * Combined type for all possible event properties.
 */
export type EventProperties = CommonProperties &
  Partial<AgentProperties> &
  Partial<DeviceProperties> &
  Partial<DiscoveryMismatchProperties> &
  Partial<StudioProperties> &
  Partial<OrganizationProperties> &
  Partial<BridgeProperties> &
  Partial<CollaborationProperties> &
  Partial<BillingProperties> &
  Partial<ControllerProperties> &
  Partial<MovementToolsProperties> &
  Partial<RecordingProperties> &
  Partial<SecurityProperties> &
  Partial<SharingProperties> &
  Partial<VideoModesProperties> &
  Partial<VideoSourcesProperties> &
  Partial<WorkspaceProperties> &
  // Multi-add analytics — batchId + per-device + summary counts.
  Partial<MultiAddProperties> &
  Partial<PrequalificationProperties> &
  Partial<DownloadProperties> &
  Partial<OnboardingProperties> &
  Partial<RendererStartupProperties> &
  T_Optional<MetadataProperties>;

/**
 * Multi-add analytics properties — analytics foundation per PRD §12.
 * Each batch event carries a `batchId` (UUID generated at click time)
 * so PostHog can join `multi_add_started` → per-device events →
 * `multi_add_completed` on a single funnel.
 *
 * Per `think-in-systems` (ai.knowledge/global): properties are the canonical
 * shape — emit sites in `emit-multi-add-analytics.ts` use these directly so
 * the catalog stays the single source of truth and downstream PostHog
 * dashboards don't drift from the renderer code.
 */
export interface MultiAddProperties {
  /**
   * UUID tying every event in a single batch together. Generated once at
   * click time (`runBulkCommit` entry) and threaded through every per-device
   * emit + the `multi_add_completed` summary. Required for funnel joins.
   */
  batchId?: string;
  /** Count the user explicitly selected before clicking "Add N sources". */
  selectedDeviceCount?: number;
  /**
   * Discovery multi-select toggle direction, carried on
   * `multi_add_discovery_selected`. `select` / `deselect` are single-row
   * checkbox toggles; `select_all` / `deselect_all` are the header
   * select-all toggle.
   */
  selectAction?: 'select' | 'deselect' | 'select_all' | 'deselect_all';
  /**
   * Count that produced a tile (includes Iris-linked + ONVIF/RTSP/NDI commits).
   * Also counts transitional placeholders, and keeps that shipped meaning so
   * existing reports stay comparable. The finalized-only count is
   * `added_device_count`.
   */
  addedDeviceCount?: number;
  /**
   * Finalized devices only: commits that landed a real Studio device. Never
   * counts transitional placeholders (`transitional_device_count`). A different
   * metric from `addedDeviceCount`, so the two must never be aliased.
   * Snake_case matches the canonical event map, like `chip_type`.
   */
  added_device_count?: number;
  /**
   * Transitional placeholders (needs-auth cameras not yet real devices),
   * reported apart from `added_device_count` on the same event.
   */
  transitional_device_count?: number;
  /**
   * Count of devices whose video AND control auth landed VALIDATED at
   * completion time. Strict subset of addedDeviceCount.
   */
  fullyConfiguredCount?: number;
  /**
   * Count of added devices that surfaced a chip (auth INVALID / control
   * INVALID / model missing / etc.). Drives the bell badge. In the
   * `multi_add_completed` summary this carries the transitory/placeholder
   * count — devices that landed a canvas tile but still need the user to
   * authenticate from Advanced Settings (`recordTransitory` in
   * `wiring.ts`). The engineer's example: "N added, of which X placeholder
   * and Y failed" — `needsAttentionCount` == `placeholderCount` == X.
   */
  needsAttentionCount?: number;
  /**
   * Count of added devices that are transitory placeholders (needs-auth
   * Iris/ONVIF cameras recorded via `recordTransitory`). A strict subset
   * of `addedDeviceCount`: the tile is on the canvas but the device is not
   * yet fully configured. Distinct from `failedCount` (never landed a
   * tile). Surfaces the engineer's "X placeholder" breakdown term.
   */
  placeholderCount?: number;
  /** Count that never produced a tile (commit threw, link rejected, skipped). */
  failedCount?: number;
  /**
   * Wall-clock duration from "Add N sources" click to runBulkCommit resolve.
   * Used to track p50/p95 batch latency.
   */
  timeToCompletionMs?: number;
  /** Video protocol id stamped on the committed device (rtsp/ndi/onvif/local). */
  protocol?: string;
  /** Organization id (workspace scope). */
  orgId?: string;
  /**
   * Iris-enabled link outcome, mapped from `LinkIrisResult.kind`/`reason`.
   * Emitted on every per-device event for Iris-enabled discoveries.
   */
  linkOutcome?: 'success' | 'locked' | 'auth' | 'already_linked' | 'error';
  /**
   * ONVIF inline-auth probe outcome, mapped from the retry state machine
   * in `runBulkCommit`. Emitted on every per-device event for ONVIF
   * discoveries.
   */
  probeOutcome?:
    | 'success_first_try'
    | 'success_after_prompt'
    | 'skipped_by_user'
    | 'skipped_after_retry'
    | 'timeout';
  /**
   * Why a device was skipped or dropped. Drives the chip copy mapping
   * and the deep-link to the right Advanced Settings section.
   *  - `auth`                       — credentials rejected
   *  - `video-failed`               — post-add bucket: video CONNECTION_STATUS_FAILED
   *  - `control-failed`             — post-add bucket: control CONNECTION_STATUS_FAILED
   *  - `no-controls`                — no PTZ control configured
   *  - `missing-model`              — fingerprint matched a generic protocol but no template
   *  - `bridge-disambiguation`      — multiple agents could host this device
   *  - `cancelled`                  — user clicked Cancel mid-batch
   *  - `already-linked`             — Iris device bound to another studio
   *  - `locked`                     — Iris device-login lockout
   *  - `commit-rejected`            — bridge replied with an error on Device.OnAdd
   *  - `discovery-stale`            — selection id had no live discovery row
   *  - `slot-trimmed`               — billing capacity short of selected count
   *  - `liveness-removed`           — placeholder evicted by the liveness
   *                                   watcher because its discovery vanished
   *  - `error`                      — unhandled rejection / network failure
   */
  skipReason?:
    | 'auth'
    | 'video-failed'
    | 'control-failed'
    | 'no-controls'
    | 'missing-model'
    | 'bridge-disambiguation'
    | 'cancelled'
    | 'already-linked'
    | 'locked'
    | 'commit-rejected'
    | 'discovery-stale'
    | 'slot-trimmed'
    | 'liveness-removed'
    | 'error';
  /**
   * Sub-tab whose needs-attention warning fired. Emitted on
   * `multi_add_subtab_needs_attention_shown` so the funnel can be
   * broken down by which aspect (auth / video / control / driver)
   * required user intervention.
   */
  subtab?: 'authentication' | 'video' | 'control' | 'driver';
  /**
   * PRD §13 alert type carried on `multi_add_alert_shown` (ENG-4486 §2.6).
   * `multiple` marks the combined-alert case; the four specific values map
   * one-to-one from the post-add buckets (auth → authentication, video-failed
   * → configure_video, control-failed → configure_control, missing-model →
   * select_model). Kept as the PRD's exact `chip_type` name so downstream
   * PostHog dashboards match the PRD funnel without a rename. The producer
   * side type is `MultiAddChipType` in
   * `notifications/hooks/multi-add-bucket-resolver.ts` — the two unions are
   * kept identical (this file cannot import the renderer type without
   * inverting the analytics→renderer layer order).
   */
  chip_type?:
    | 'authentication'
    | 'configure_video'
    | 'configure_control'
    | 'select_model'
    | 'multiple';
  /**
   * Chip variant the user clicked, carried on `multi_add_chip_clicked`.
   * Splits authenticate (needs-attention) vs partial (added-with-warnings)
   * vs failed-recovery clicks in the funnel.
   */
  chipVariant?: 'needs-attention' | 'added-with-warnings' | 'failed';
  /**
   * Terminal outcome of a single-device add commit, carried on
   * `single_add_completed`. Enables single-vs-multi comparison + single-add
   * RTSP failure tracking.
   */
  singleAddOutcome?: 'success' | 'error';
}
